/* ============================================================
   Dathent — Batch detail view
   Live progress feed for a single generation batch.
   Ported from design pack pages/batch.jsx → real API wiring
   over ContentFeedContext + api.content.getBatch.
   ============================================================ */
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons, BrandTints } from '../components/pack-icons/brands';
import { useContentFeed } from '../hooks/useContentFeed.js';
import { api } from '../services/api/client.js';

const ACTIVE_BATCH_STATUSES = new Set(['queued', 'generating', 'reserved']);
const READY_ITEM_STATUSES = new Set(['ready', 'scheduled', 'publishing', 'published']);

const PILLAR_COLORS = ['#6E5BFF', '#4FC3FF', '#E455CF', '#F5B544', '#15803D', '#FF8B6B'];

function tileStatus(item) {
  const s = item?.status;
  if (s === 'failed') return 'failed';
  // "Ready" only when text AND visual are both done. With
  // VISUAL_QUEUE_ENABLED=1 the text portion can finish (and the item
  // hits status='ready' / 'scheduled' / 'published') before the image
  // gen callback fires — that used to render a tile labelled "Ready"
  // while the image was still being composed by DALL-E. Surfacing
  // visual_status lets us keep the composing affordance up.
  if (READY_ITEM_STATUSES.has(s)) {
    if (isImageStillCooking(item)) return 'composing';
    return 'ready';
  }
  const stage = (item?.generationStage || '').toLowerCase();
  const prog = Number(item?.generationProgress ?? 0);
  if (stage === 'queued' || prog < 25) return 'queued';
  if (prog < 70) return 'drafting';
  return 'composing';
}

/**
 * Is the image_post visual still in flight, even though the item's
 * lifecycle status moved past 'generating'? Returns true when:
 *   - the job needs a visual (format='image_post' on the original job
 *     or on the content_item),
 *   - the visual hasn't been finalized (visual_status missing, or
 *     'queued' / 'processing' / 'reserved'),
 *   - AND no media URL has been attached yet (defensive fallback so we
 *     don't keep the spinner up if the visual actually landed but
 *     visual_status didn't refresh).
 */
function isImageStillCooking(item) {
  const format = item?.jobFormat || item?.format || item?.raw?.contentItem?.format;
  if (format !== 'image_post') return false;
  const vs = (item?.visualStatus || '').toLowerCase();
  if (vs === 'succeeded' || vs === 'failed') return false;
  // No visual_status set yet (older rows) — fall back to "is there media".
  const hasMedia = Boolean(
    item?.imageUrl ||
      (Array.isArray(item?.mediaUrls) && item.mediaUrls.length > 0) ||
      item?.raw?.activeVersion?.imageUrl ||
      (Array.isArray(item?.raw?.activeVersion?.mediaUrls) && item.raw.activeVersion.mediaUrls.length > 0)
  );
  if (hasMedia) return false;
  return true;
}

function fmtTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

/* Return the actual content pillar name when the backend assigned one
   (set on content_items.content_pillar from the strategy). Returns null
   when no explicit pillar is set — the previous fallback that cycled
   through ['Topic','Hook','Visual','Body','CTA'] was a placeholder that
   didn't correspond to the user's real strategy and just confused
   readers in the batch grid. */
function pickPillar(item, index) {
  const explicit = item?.raw?.contentItem?.contentPillar || item?.contentPillar;
  if (!explicit) return null;
  return {
    name: explicit,
    color: PILLAR_COLORS[index % PILLAR_COLORS.length],
  };
}

// Per-platform body picker. Backend ships content_versions.platform_versions_json
// with variants for IG/FB/Threads/Telegram/etc — list view compacts to 240 chars
// per platform. Falls back to shared body/caption/hook when no variant exists.
function platformBody(v, platform) {
  if (!v || !platform) return null;
  const pv = v.platformVersions || {};
  const aliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const direct = pv[platform];
  if (typeof direct === 'string' && direct.trim()) return direct;
  if (direct && typeof direct === 'object' && typeof direct.body === 'string' && direct.body.trim()) return direct.body;
  if (aliasKey) {
    const aliased = pv[aliasKey];
    if (typeof aliased === 'string' && aliased.trim()) return aliased;
    if (aliased && typeof aliased === 'object' && typeof aliased.body === 'string' && aliased.body.trim()) return aliased.body;
  }
  return null;
}

function snippetOf(item, platform = null) {
  const r = item?.raw || {};
  const v = r.activeVersion || {};
  const platformText = platformBody(v, platform);
  return platformText || v.body || v.caption || v.hook || item?.snippet || '';
}

function imageOf(item) {
  const r = item?.raw || {};
  const v = r.activeVersion || {};
  return v.imageUrl || (Array.isArray(v.mediaUrls) ? v.mediaUrls[0] : null) || null;
}

/**
 * Expand a batch content item into per-PLATFORM tiles (not per-target).
 *
 * A single content_item that broadcasts to 2 Telegram channels still
 * shows as ONE tile labelled "Telegram · 2 channels" — that matches the
 * user's mental model where the same draft going to multiple Telegram
 * channels is one logical post, not two. Same for any platform with
 * multiple accounts: render one tile per platform with a channel-count
 * badge when >1.
 *
 * Pre-publish (no targets yet) we use the item's own platforms — for
 * language-split batches, each item only targets the platforms in its
 * own language group (EN item → only IG; UK item → only TG). Never fall
 * back to batch.platforms here: that would double-count a 2-platform /
 * 2-language batch as 4 tiles before targets land.
 */
function expandBatchItemToTiles(item, batchPlatforms) {
  const raw = item?.raw || {};
  const targets = Array.isArray(raw.publishSummary?.targets) ? raw.publishSummary.targets : [];

  if (targets.length > 0) {
    // Group publish_targets by normalized platform name so a content_item
    // posting to N Telegram channels collapses to ONE Telegram tile.
    const byPlatform = new Map();
    for (const t of targets) {
      const p = t.platform || item.platform || 'instagram';
      if (!byPlatform.has(p)) byPlatform.set(p, []);
      byPlatform.get(p).push(t);
    }
    const tiles = [];
    for (const [platform, group] of byPlatform.entries()) {
      // Pick a representative status / scheduled time / published time:
      // - 'published' wins (anything published on this platform = ready)
      // - else any 'failed' = failed
      // - else the latest scheduled/queued is fine
      const rep = group.find((t) => t.status === 'published')
        || group.find((t) => t.status === 'failed')
        || group[0];
      tiles.push({
        ...item,
        tileKey: `${item.id}::${platform}`,
        platform,
        // Aggregate target metadata so the tile click still has enough info
        // to route to a preview / edit and the tile can render a
        // "N channels" badge.
        targetId: rep.id || null,
        targetStatus: rep.status || null,
        targetScheduledAt: rep.scheduledAt || null,
        targetPublishedAt: rep.publishedAt || null,
        targetCount: group.length,
        targetIds: group.map((t) => t.id).filter(Boolean),
      });
    }
    return tiles;
  }
  // Pre-publish: prefer the item's own platforms.
  const itemPlatforms = Array.isArray(item.platforms) && item.platforms.length
    ? item.platforms
    : (item.platform ? [item.platform] : null);
  const platforms = itemPlatforms || (batchPlatforms && batchPlatforms.length ? batchPlatforms : ['instagram']);
  return platforms.map((p) => ({
    ...item,
    tileKey: `${item.id}::${p}`,
    platform: p,
    targetId: null,
    targetStatus: null,
    targetScheduledAt: null,
    targetPublishedAt: null,
    targetCount: 0,
    targetIds: [],
  }));
}

/* ---------------- Hero ---------------- */
function BatchHero({ batch, items, onOpenLibrary, navigate }) {
  const total = Math.max(1, Number(batch?.totalItems || items.length || 0));
  const ready = items.filter((p) => tileStatus(p) === 'ready').length;
  const inflight = items.filter((p) => ['drafting', 'composing'].includes(tileStatus(p))).length;
  const queued = items.filter((p) => tileStatus(p) === 'queued').length;
  const failed = items.filter((p) => tileStatus(p) === 'failed').length;
  // Prefer the batch row's `progress` field — it's an average of each
  // job's stage (25% drafting → 70% visualizing → 100% ready) so the bar
  // crawls forward even before any single job hits "ready". Falls back to
  // ready/total when the batch row hasn't been hydrated yet, and never
  // dips below the previous floor: ready ≥ 1 always means at least the
  // proportional share of completed items.
  const backendPct = typeof batch?.progress === 'number' ? Math.round(batch.progress) : null;
  const itemsPct = Math.round((ready / total) * 100);
  const pct = Math.max(5, Math.min(100, Math.max(backendPct ?? itemsPct, itemsPct)));
  // Two paths to "all done":
  // (a) backend confirms via batch.status (most authoritative), OR
  // (b) all items have reached a terminal state (covers the window where
  //     feed.batches hasn't yet propagated the new batch.status to FE
  //     local state — without this, the UI sits stuck on "Live · 2 of 2"
  //     for a poll cycle even though every post is ready).
  const itemsAllDone = items.length > 0 && items.every((p) => {
    const status = tileStatus(p);
    return status === 'ready' || status === 'failed' || status === 'scheduled' || status === 'published';
  });
  const allDone = itemsAllDone || (!ACTIVE_BATCH_STATUSES.has(batch?.status) && ready >= total - failed);
  // Platforms this batch targets. Each content_item fans out to one
  // publish_target per platform, so a 1-post / 3-platform batch becomes
  // 3 cards in Library — surface that up-front so the count is not
  // confusing.
  const platforms = Array.isArray(batch?.platforms) && batch.platforms.length
    ? batch.platforms
    : [];
  const totalTargets = total * Math.max(1, platforms.length || 1);

  return (
    <section className="bv-hero">
      <div className="bv-hero-bg" />
      <div className="bv-hero-l">
        <div className="bv-hero-mark">
          <span className="bv-hero-spin"><I.sparkles size={20} /></span>
        </div>
        <div className="bv-hero-info">
          <div className="bv-hero-top">
            <span className="pill pill-accent">
              {allDone ? (
                <><span className="dot" />Complete</>
              ) : (
                // "Live · K of N" where K is the item currently being worked
                // on — clamped so we never overshoot total (avoids the
                // "3 of 2" oddity when every job is ready but the batch
                // row hasn't transitioned out of the active status yet).
                <><span className="dot pulse" />Live · {Math.min(ready + 1, total)} of {total}</>
              )}
            </span>
            <span className="bv-hero-id mono">batch · #{batch?.id?.slice(0, 8) || '—'}</span>
          </div>
          <h1 className="bv-hero-h">
            {batch?.topic || 'Generation batch'} —{' '}
            <span className="bv-hero-h-ac">{total} post{total === 1 ? '' : 's'}</span>
          </h1>
          <div className="bv-hero-meta">
            <span><I.clock size={11} /> Started {fmtTime(batch?.createdAt)}</span>
            <span className="bv-d-sep">·</span>
            <span><I.sparkles size={11} /> {allDone ? 'Done' : 'In progress'}</span>
            {failed > 0 && (
              <>
                <span className="bv-d-sep">·</span>
                <span style={{ color: 'var(--danger-text)' }}>{failed} failed</span>
              </>
            )}
          </div>
          {platforms.length > 0 && (
            <div className="bv-hero-platforms" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-subtle)' }}>
                Targets {totalTargets}
              </span>
              {platforms.length > 1 && (
                <span style={{ color: 'var(--text-subtle)', fontSize: 11 }}>·</span>
              )}
              <div style={{ display: 'inline-flex', gap: 6 }}>
                {platforms.map((p) => {
                  const tint = BrandTints[p] || BrandTints.x;
                  return (
                    <span
                      key={p}
                      title={p}
                      className={`ob-net-ic-${p}`}
                      style={{
                        width: 22, height: 22, borderRadius: 5,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff',
                        background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`,
                      }}
                    >
                      {BrandIcons[p]?.({ size: 12 })}
                    </span>
                  );
                })}
              </div>
              {platforms.length > 1 && (
                <span style={{ color: 'var(--text-subtle)', fontSize: 12 }}>
                  each post goes to {platforms.length} networks
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="bv-hero-r">
        <div className="bv-summary">
          <div className="bv-sum">
            <div className="bv-sum-l">Ready</div>
            <div className="bv-sum-v"><b>{ready}</b><span> / {total}</span></div>
          </div>
          <div className="bv-sum">
            <div className="bv-sum-l">In flight</div>
            <div className="bv-sum-v"><b>{inflight}</b></div>
          </div>
          <div className="bv-sum">
            <div className="bv-sum-l">Queued</div>
            <div className="bv-sum-v"><b>{queued}</b></div>
          </div>
        </div>

        <div className={`bv-progress ${allDone ? 'is-done' : ''}`}>
          <div className="bv-progress-track">
            <div className="bv-progress-fill" style={{ width: pct + '%' }} />
          </div>
          <span className="mono bv-pct">{pct}%</span>
        </div>

        <div className="bv-hero-actions">
          <button className="btn btn-ghost btn-sm" type="button" onClick={onOpenLibrary}>
            Open Library <I.arrowRight size={12} />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Workflow rail ---------------- */
function WorkflowRail({ batch, items }) {
  const ready = items.filter((p) => tileStatus(p) === 'ready').length;
  const total = Math.max(1, Number(batch?.totalItems || items.length || 0));
  const current = items.find((p) => ['drafting', 'composing'].includes(tileStatus(p)));
  const curStatus = current ? tileStatus(current) : null;
  // Mirror BatchHero's items-driven complete check — when the cached
  // batch.status is stale (FE feed hasn't refreshed yet) we still want
  // every workflow row to flip to "done" so the rail doesn't lie about
  // "Image gen running" or "Auto-scheduling" when those phases already
  // finished.
  const itemsAllDone = items.length > 0 && items.every((p) => {
    const status = tileStatus(p);
    return status === 'ready' || status === 'failed' || status === 'scheduled' || status === 'published';
  });
  const allDone = itemsAllDone || (!ACTIVE_BATCH_STATUSES.has(batch?.status) && ready >= total);
  // Map the batch's schedule mode to the bottom workflow row's copy and
  // its "done" state. Posts saved as draft never get scheduled — showing
  // "Auto-schedule" / Calendar branding for them is misleading.
  const scheduleMode = batch?.scheduleMode || 'draft';
  const scheduleStep = (() => {
    if (scheduleMode === 'draft') {
      return {
        label: 'Save as draft',
        detail: allDone ? 'Saved to Library' : 'Pending',
        done: allDone,
      };
    }
    if (scheduleMode === 'now' || scheduleMode === 'publish_now') {
      return {
        label: 'Publish now',
        detail: allDone ? 'Sent to platforms' : 'Pending',
        done: allDone,
      };
    }
    if (scheduleMode === 'hybrid') {
      return {
        label: 'Publish + schedule',
        detail: allDone ? 'First posted, rest queued' : 'Pending',
        done: allDone,
      };
    }
    // schedule / spread / scheduled — anything calendar-driven
    return {
      label: 'Slot into calendar',
      detail: allDone ? 'Scheduled in calendar' : 'Auto-schedule',
      done: allDone,
    };
  })();

  const steps = [
    {
      id: 'plan',
      label: 'Plan topic clusters',
      detail: `${total} topics`,
      done: true,
    },
    {
      id: 'hooks',
      label: 'Outline hooks per pillar',
      detail: 'Angles per pillar',
      done: items.length > 0,
    },
    {
      id: 'copy',
      label: 'Write post copy',
      // Clamp ready+1 so "Post 3 of 2" never appears when everything's done.
      // When complete, swap to a static "X posts" detail so the line reads
      // as a finished milestone instead of an ongoing counter.
      detail: allDone ? `${total} posts written` : `Post ${Math.min(ready + 1, total)} of ${total}`,
      doing: curStatus === 'drafting',
      done: allDone || ready >= total,
    },
    {
      id: 'image',
      label: 'Compose visual prompts',
      // "Image gen running" was a static string that hung around AFTER
      // visuals were done — confusing when the user sees it under a
      // "complete" batch. Flip the copy based on actual state.
      detail: allDone ? 'Visuals attached' : (curStatus === 'composing' ? 'Image gen running' : 'Queued'),
      doing: curStatus === 'composing',
      done: allDone || ready >= total,
    },
    {
      id: 'sched',
      ...scheduleStep,
      queued: !allDone,
    },
  ];

  return (
    <aside className="bv-rail card">
      <div className="bv-rail-h">
        <div className="eyebrow"><span>Workflow</span></div>
        <h3>What's happening</h3>
      </div>
      <div className="bv-steps">
        {steps.map((s) => {
          const status = s.done ? 'done' : s.doing ? 'doing' : 'queued';
          return (
            <div key={s.id} className={`bv-step bv-step-${status}`}>
              <span className="bv-step-rail-line" />
              <span className="bv-step-ico">
                {status === 'done' && <I.check size={11} />}
                {status === 'doing' && <span className="dot pulse" style={{ background: 'currentColor' }} />}
                {status === 'queued' && <span className="dot" style={{ background: 'currentColor', opacity: 0.5 }} />}
              </span>
              <div className="bv-step-text">
                <div className="bv-step-label">{s.label}</div>
                <div className="bv-step-detail">{s.detail}</div>
              </div>
              {status === 'doing' && (
                <div className="bv-step-stream">
                  <span className="bv-step-stream-bar" />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {current && (
        <div className="bv-rail-foot">
          <span className="eyebrow"><span>Now generating</span></span>
          <div className="bv-now">
            <span
              className={`ob-net-ic-${current.platform}`}
              style={{
                width: 22, height: 22, borderRadius: 4,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
              }}
            >
              {BrandIcons[current.platform]?.({ size: 12 })}
            </span>
            <div className="bv-now-info">
              <div className="bv-now-title">{current.title || 'Generating…'}</div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/* ---------------- Post tile ---------------- */
// Each tile now represents ONE publish target (platform + account), not the
// whole content_item. expandBatchItemToTiles() above produces one of these
// per platform of the batch. Click → /app/post/:id?platform=…&target=…
// so the user lands on the right phone chrome (Threads vs Telegram vs IG vs
// FB) for that specific publication, not a generic preview.
function PostTile({ tile, index, onClick }) {
  // Tile-level status: when we have a publish_target, prefer its status
  // (queued/scheduled/published/failed) so the user sees the per-platform
  // shipping outcome — not the parent item's overall state.
  const baseStatus = tileStatus(tile);
  const targetStatus = tile.targetStatus;
  const status = targetStatus === 'published' ? 'ready'
    : targetStatus === 'failed' ? 'failed'
    : targetStatus === 'queued' || targetStatus === 'pending' || targetStatus === 'claimed' || targetStatus === 'scheduled' ? 'ready'
    : baseStatus;
  const pillar = pickPillar(tile, index);
  const platform = tile.platform || 'instagram';
  const tint = BrandTints[platform] || BrandTints.x;
  const realImg = imageOf(tile);
  const snippet = snippetOf(tile, platform);
  const title = tile.title || 'Untitled';

  const coverStyle = realImg
    ? { background: `linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%), url("${realImg}") center/cover no-repeat` }
    : { background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)` };

  return (
    <article
      className={`bv-tile bv-tile-${status}`}
      onClick={onClick}
      style={{ cursor: status === 'ready' ? 'pointer' : 'default' }}
    >
      <div className="bv-tile-cover">
        {status === 'ready' && (
          <div className="bv-tile-img" style={coverStyle}>
            {!realImg && (
              <div className="bv-tile-img-cap" style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>
                {title.split(' ').slice(0, 4).join(' ')}
              </div>
            )}
          </div>
        )}
        {/* Mini-phone loader for in-flight tiles. Mirrors the larger
            generating-state UI used in PostPreview so the batch grid and the
            single-post preview share the same vocabulary. The phone shape +
            spinning loader make it unambiguous that the post is being
            generated — previously the skeleton+shimmer looked very similar
            to "ready" empty state, which confused users into thinking it
            was done. */}
        {(status === 'composing' || status === 'drafting' || status === 'queued') && (
          <div className="bv-tile-img bv-tile-phone-stage">
            <div className="bv-tile-phone">
              <div className="bv-tile-phone-notch" />
              <div className={`bv-tile-phone-screen bv-tile-phone-${platform}`}>
                <div className="bv-tile-phone-spinner" />
                <div className="bv-tile-phone-label">
                  {status === 'composing' && <span>Composing image · {Math.round(Number(tile.generationProgress || 0))}%</span>}
                  {status === 'drafting' && <span>Writing copy<span className="bv-tile-phone-dots">…</span></span>}
                  {status === 'queued' && <span>Waiting in queue<span className="bv-tile-phone-dots">…</span></span>}
                </div>
              </div>
              <div className="bv-tile-phone-home" />
            </div>
          </div>
        )}
        {status === 'failed' && (
          <div className="bv-tile-img" style={{ background: 'var(--danger-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <I.alert size={24} />
          </div>
        )}

        <span className="bv-tile-slot mono">#{String(index + 1).padStart(2, '0')}</span>

        {/* Platform badge — one tile = one platform (regardless of how many
            channels/accounts on it). When the same content broadcasts to
            multiple accounts on the same platform (e.g. 2 Telegram channels),
            we surface "·2" next to the icon so the user knows. */}
        <span
          className="bv-tile-plat"
          style={{
            display: 'inline-flex', gap: 4, padding: 4, alignItems: 'center',
            background: 'rgba(0,0,0,0.35)', borderRadius: 6,
            backdropFilter: 'blur(6px)',
          }}
          title={tile.targetCount > 1 ? `${platform} · ${tile.targetCount} channels` : platform}
        >
          <span
            className={`ob-net-ic-${platform}`}
            style={{
              width: 18, height: 18, borderRadius: 4,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff',
              background: `linear-gradient(135deg, ${tint.from} 0%, ${tint.to} 100%)`,
            }}
          >
            {BrandIcons[platform]?.({ size: 10 })}
          </span>
          {tile.targetCount > 1 && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', paddingRight: 2, fontVariantNumeric: 'tabular-nums' }}>
              ×{tile.targetCount}
            </span>
          )}
        </span>

        <span className={`bv-tile-status bv-tile-status-${status}`}>
          {status === 'ready'     && <><I.check size={9} /> Ready</>}
          {status === 'composing' && <><span className="dot pulse" /> Composing</>}
          {status === 'drafting'  && <><span className="dot pulse" /> Drafting</>}
          {status === 'queued'    && <><span className="dot pulse" style={{ opacity: 0.6 }} /> Queued</>}
          {status === 'failed'    && <><I.alert size={9} /> Failed</>}
        </span>
      </div>

      <div className="bv-tile-body">
        {status === 'queued' ? (
          <div className="bv-tile-skel-lines">
            <div className="bv-tile-skel-line w-80" />
            <div className="bv-tile-skel-line w-60" />
          </div>
        ) : (
          <>
            <div className="bv-tile-title">{title}</div>
            {snippet && <div className="bv-tile-snippet">{snippet.slice(0, 140)}</div>}
          </>
        )}
        <div className="bv-tile-foot">
          {pillar ? (
            <span className="bv-tile-pillar">
              <span className="bv-pillar-dot" style={{ background: pillar.color }} />
              <span>{pillar.name}</span>
            </span>
          ) : (
            <span className="bv-tile-pillar" style={{ color: 'var(--text-subtle)', textTransform: 'capitalize' }}>
              {platform}
            </span>
          )}
          <span className="bv-tile-time mono">{tile.date || ''}</span>
        </div>
      </div>
    </article>
  );
}

/* ---------------- Filter bar ---------------- */
function FilterBar({ items, filter, setFilter, view, setView }) {
  const counts = {
    all: items.length,
    ready: items.filter((p) => tileStatus(p) === 'ready').length,
    inflight: items.filter((p) => ['drafting', 'composing'].includes(tileStatus(p))).length,
    queued: items.filter((p) => tileStatus(p) === 'queued').length,
    failed: items.filter((p) => tileStatus(p) === 'failed').length,
  };
  const filters = [
    { id: 'all',      label: 'All' },
    { id: 'inflight', label: 'In flight' },
    { id: 'ready',    label: 'Ready' },
    { id: 'queued',   label: 'Queued' },
  ];
  if (counts.failed > 0) filters.push({ id: 'failed', label: 'Failed' });
  return (
    <div className="bv-filterbar">
      <div className="bv-filters">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`bv-filter ${filter === f.id ? 'is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="bv-filter-c mono">{counts[f.id]}</span>
          </button>
        ))}
      </div>
      <div className="bv-spacer" />
      <div className="bv-view">
        <button type="button" className={view === 'grid' ? 'is-active' : ''} onClick={() => setView('grid')} title="Grid"><I.grid size={13} /></button>
        <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')} title="List"><I.list size={13} /></button>
      </div>
    </div>
  );
}

/* ---------------- Top-level Batch ---------------- */
export const Batch = () => {
  const { id: batchId } = useParams();
  const navigate = useNavigate();
  const { content, activeBatches, refreshContent } = useContentFeed();

  const [filter, setFilter] = useState('all');
  const [view, setView] = useState('grid');
  const [serverBatch, setServerBatch] = useState(null);
  const [error, setError] = useState(null);

  // Find batch first from feed's active list, fall back to API call (settled batches).
  useEffect(() => {
    if (!batchId) return;
    const inFeed = activeBatches.find((b) => b.id === batchId);
    if (inFeed) {
      setServerBatch(inFeed);
      return;
    }
    let cancelled = false;
    api.content.getBatch(batchId)
      .then((res) => {
        if (cancelled) return;
        setServerBatch(res?.batch || res?.data?.batch || res?.data || res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Batch not found');
      });
    return () => { cancelled = true; };
  }, [batchId, activeBatches]);

  // Always prefer the active batch record (has live counts).
  const batch = useMemo(() => {
    const live = activeBatches.find((b) => b.id === batchId);
    return live || serverBatch;
  }, [activeBatches, serverBatch, batchId]);

  // Items belonging to this batch (from feed; refresh keeps them current).
  const items = useMemo(
    () => content.filter((c) => c.generationBatchId === batchId),
    [content, batchId]
  );

  const filtered = useMemo(() => {
    if (filter === 'all') return items;
    if (filter === 'inflight') return items.filter((p) => ['drafting', 'composing'].includes(tileStatus(p)));
    return items.filter((p) => tileStatus(p) === filter);
  }, [items, filter]);

  if (error) {
    return (
      <div className="bv-root">
        <div className="card" style={{ padding: 24 }}>
          <h2>Batch not found</h2>
          <p style={{ color: 'var(--text-muted)' }}>{error}</p>
          <button className="btn btn-soft btn-sm" onClick={() => navigate('/app/library')}>
            Back to Library
          </button>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="bv-root">
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
          Loading batch…
        </div>
      </div>
    );
  }

  return (
    <div className="bv-root">
      <div className="bv-back-row">
        <button className="bv-back" type="button" onClick={() => navigate('/app/create')}>
          <I.chevLeft size={14} /> Create Post
        </button>
      </div>

      <BatchHero
        batch={batch}
        items={items}
        onOpenLibrary={() => navigate('/app/library')}
        navigate={navigate}
      />

      <div className="bv-body">
        <div className="bv-main">
          <FilterBar items={items} filter={filter} setFilter={setFilter} view={view} setView={setView} />
          {items.length === 0 ? (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>
              Posts will appear here as they generate. The pill in the topbar tracks progress.
            </div>
          ) : (
            <div className={`bv-grid ${view === 'list' ? 'is-list' : ''}`}>
              {filtered.flatMap((item) => expandBatchItemToTiles(item, Array.isArray(batch?.platforms) ? batch.platforms : null))
                .map((tile, idx) => (
                  <PostTile
                    key={tile.tileKey}
                    tile={tile}
                    index={idx}
                    onClick={() => {
                      // Open this exact platform/target preview. PostPreview
                      // reads ?platform= + ?target= to pick the right chrome
                      // and the correct platform_versions[platform] body.
                      const params = new URLSearchParams();
                      if (tile.targetId) params.set('target', tile.targetId);
                      if (tile.platform) params.set('platform', tile.platform);
                      const qs = params.toString();
                      const itemStatus = tileStatus(tile);
                      if (itemStatus === 'ready' || tile.targetStatus === 'published' || tile.targetStatus === 'scheduled') {
                        navigate(`/app/post/${tile.id}${qs ? `?${qs}` : ''}`);
                      }
                    }}
                  />
                ))}
            </div>
          )}
        </div>

        <WorkflowRail batch={batch} items={items} />
      </div>
    </div>
  );
};

export default Batch;
